import {
  ExportKpiLogsDto,
  ExportKpiLogsVm,
  GetKpiMetricsDto,
  KpiMetricsVm,
  KpiLogVm,
} from "@/types/kpi.types";
import baseApi from "./RootBaseApi";

export const KpiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEngagementMetrics: builder.mutation<
      KpiMetricsVm,
      URLSearchParams | Partial<GetKpiMetricsDto>
    >({
      query: (filters) => {
        if (filters instanceof URLSearchParams) {
          const queryString = filters.toString();
          return {
            url: `kpi/metrics${queryString ? `?${queryString}` : ""}`,
            method: "GET",
          };
        } else {
          const { startDate, endDate, kpiType, role } = filters || {};
          const params = new URLSearchParams();

          if (startDate) params.append("startDate", startDate.toISOString());
          if (endDate) params.append("endDate", endDate.toISOString());
          if (kpiType !== undefined) {
            const kpiTypeNum =
              typeof kpiType === "string" ? parseInt(kpiType, 10) : kpiType;
            if (!isNaN(kpiTypeNum)) {
              params.append("kpiType", kpiTypeNum.toString());
            }
          }
          if (role) params.append("role", role);

          const queryString = params.toString();
          return {
            url: `kpi/metrics${queryString ? `?${queryString}` : ""}`,
            method: "GET",
          };
        }
      },
      transformResponse: (response: KpiMetricsVm) => response,
    }),

    exportKpiLogsJson: builder.mutation<ExportKpiLogsVm, ExportKpiLogsDto>({
      query: (body) => ({
        url: "kpi/export/json",
        method: "POST",
        data: body,
      }),
      transformResponse: (response: ExportKpiLogsVm) => response,
    }),

    exportKpiLogsCsv: builder.mutation<string, ExportKpiLogsDto>({
      query: (dto) => ({
        url: "/kpi/export/csv",
        method: "POST",
        data: dto,
        responseHandler: async (response: Response): Promise<string> => {
          return response.text();
        },
      }),
    }),

    getAllKpiLogs: builder.query<
      {
        data: KpiLogVm[];
        totalItems: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
      },
      {
        page: number;
        limit: number;
        startDate?: Date;
        endDate?: Date;
        kpiType?: string;
        role?: string;
      }
    >({
      query: ({ page, limit, ...filters }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (filters.startDate)
          params.append("startDate", filters.startDate.toISOString());
        if (filters.endDate)
          params.append("endDate", filters.endDate.toISOString());
        if (filters.kpiType)
          params.append("kpiType", parseInt(filters.kpiType, 10).toString());
        if (filters.role) params.append("role", filters.role);

        return {
          url: `kpi/logs?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response: {
        data: KpiLogVm[];
        totalItems: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
      }) => response,
    }),
  }),
});

export const {
  useGetEngagementMetricsMutation,
  useExportKpiLogsJsonMutation,
  useExportKpiLogsCsvMutation,
  useGetAllKpiLogsQuery,
} = KpiService;
